import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Clock, Users, ArrowRight, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

export default function Courses() {
  const courses = [
    { title: "Introduction to Workforce Intelligence", duration: "4 weeks", level: "Beginner", students: 1250 },
    { title: "AI-Powered Leadership", duration: "6 weeks", level: "Intermediate", students: 890 },
    { title: "Organizational Diagnostics Fundamentals", duration: "4 weeks", level: "Beginner", students: 1100 },
    { title: "Change Management in the AI Era", duration: "8 weeks", level: "Advanced", students: 650 },
    { title: "Workforce Analytics & Metrics", duration: "6 weeks", level: "Intermediate", students: 780 },
    { title: "Safety Management Systems", duration: "8 weeks", level: "Professional", students: 520 },
    { title: "Quality Auditing Essentials", duration: "4 weeks", level: "Beginner", students: 920 },
    { title: "Strategic Workforce Planning", duration: "6 weeks", level: "Advanced", students: 430 }
  ];

  const getLevelColor = (level) => {
    switch(level) {
      case 'Beginner': return 'bg-[#3B82F6]/10 text-[#3B82F6]';
      case 'Intermediate': return 'bg-[#1E3A8A]/10 text-[#1E3A8A]';
      case 'Advanced': return 'bg-[#991B1B]/10 text-[#991B1B]';
      default: return 'bg-[#60A5FA]/10 text-[#60A5FA]';
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="courses-page">
      {/* Hero */}
      <section className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/programs" className="inline-flex items-center gap-2 text-[#1E3A8A] hover:text-[#991B1B] mb-6 text-sm">
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Programs
            </Link>
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] text-sm font-medium mb-6">
                <BookOpen className="w-4 h-4" />
                Flexible Learning
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6" style={{ color: '#991B1B' }}>
                Courses
              </h1>
              <p className="text-lg text-[#1E3A8A] leading-relaxed">
                Focused courses covering specific topics in workforce readiness, AI applications, 
                leadership, safety, and operational excellence.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {courses.map((course, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-[#E5E7EB] bg-white hover:shadow-lg transition-shadow group cursor-pointer">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(course.level)}`}>
                        {course.level}
                      </span>
                      <div className="w-8 h-8 rounded-full bg-[#1E3A8A]/10 flex items-center justify-center group-hover:bg-[#1E3A8A] transition-colors">
                        <Play className="w-4 h-4 text-[#1E3A8A] group-hover:text-white transition-colors" />
                      </div>
                    </div>
                    <h3 className="font-heading text-base font-semibold mb-3 leading-tight" style={{ color: '#991B1B' }}>
                      {course.title}
                    </h3>
                    <div className="flex items-center gap-4 text-xs text-[#1E3A8A]">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {course.duration}
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {course.students.toLocaleString()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
            Start Learning Today
          </h2>
          <p className="text-lg text-[#1E3A8A] mb-8">
            Flexible, self-paced courses to build your workforce intelligence skills.
          </p>
          <Link to="/admissions">
            <Button size="lg" className="bg-[#991B1B] text-white hover:bg-[#7F1D1D] h-12 px-8">
              Enroll Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
